/**
 * Voice pill selection (pills rewrite P6) — LOCAL, zero network.
 *
 * This module once POSTed /api/widget/rank-page-questions (a spend-gated
 * mini that reordered candidates). That endpoint is deleted: the verified
 * nudge pool is pre-ranked at generation and the pageview deal already
 * randomizes presentation, so selection is now a pure re-order — candidates
 * that appear in the pool's current deal come first, in deal order. The
 * exported surface is unchanged so both voice call sites compile untouched;
 * `source` reports "nudge_pool" for telemetry continuity.
 */
import { nudgePool } from "./nudge-pool.ts";

export type RankPageQuestionsContext = {
  lastUser?: string;
  lastAgentPreview?: string;
  pathStack?: string[];
};

export type RankPageQuestionsResponse = {
  ranked: string[];
  pick: string | null;
  source: string;
};

/** Kept for call-site compatibility; nothing waits on the network now. */
export const RANK_PAGE_QUESTIONS_TIMEOUT_MS = 0;

function normalizeAsk(q: string): string {
  return (q ?? "").replace(/\s+/g, " ").trim().toLowerCase();
}

/** Keep only strings present in the candidate inventory, in ranked order,
 *  deduped — the never-invent rule for spoken pills (unchanged contract). */
export function filterToCandidates(
  ranked: readonly string[],
  candidates: readonly string[],
): string[] {
  const byNorm = new Map<string, string>();
  for (const c of candidates) {
    const k = normalizeAsk(c);
    if (k && !byNorm.has(k)) byNorm.set(k, c);
  }
  const out: string[] = [];
  const seen = new Set<string>();
  for (const r of ranked) {
    const k = normalizeAsk(r);
    const orig = byNorm.get(k);
    if (!orig || seen.has(k)) continue;
    seen.add(k);
    out.push(orig);
  }
  return out;
}

export async function fetchRankedPageQuestions(input: {
  apiOrigin?: string;
  url?: string;
  candidates: readonly string[];
  max: number;
  context?: RankPageQuestionsContext;
}): Promise<RankPageQuestionsResponse> {
  // Pool deal order first (never-invent holds: only caller candidates rank),
  // then any candidates outside the pool in their given order.
  const inPoolOrder = filterToCandidates(nudgePool(), input.candidates);
  const rest = input.candidates.filter(
    (c) => !inPoolOrder.some((p) => normalizeAsk(p) === normalizeAsk(c)),
  );
  const ranked = [...inPoolOrder, ...rest].slice(0, Math.max(input.max, 1));
  return { ranked, pick: ranked[0] ?? null, source: "nudge_pool" };
}
